import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { exchangeGoogleToken, type AuthUser } from '../utils/auth';
import GoogleLoginButton from './GoogleLoginButton';
import { grantTermsConsent } from '../hooks/useConsent';
import { identifyUser, trackEvent } from '../analytics/posthog';
import { setIdentity, setIdentityName } from '../utils/identity';
import { checkRoom, type RoomCheckResult } from '../utils/room';
import { loadSession } from '../utils/session';
import { useI18n } from '../i18n/useI18n';
import KaraokrLogo from './KaraokrLogo';

interface Props {
  roomId: string;
  mode: 'singer' | 'host';
  isJoining?: boolean;
  error?: string | null;
  onJoin: (opts: { name: string; password?: string; user?: AuthUser }) => void;
  onGoToSinger?: () => void;
}

export default function RoomJoinGate({ roomId, mode, isJoining, error: externalError, onJoin, onGoToSinger }: Props) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState<RoomCheckResult | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState(() => {
    const sesh = loadSession();
    return sesh?.roomId === roomId ? (sesh.password || '') : '';
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    checkRoom(roomId)
      .then((data) => {
        if (cancelled) return;
        setRoom(data);
        if (!data.exists && mode === 'singer') {
          setError(t('room.error.notFound'));
        } else if (!data.isActive && data.exists) {
          setError(t('room.error.ended'));
        }
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : t('room.error.serviceUnreachable');
        setError(msg);
      })
      .finally(() => setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [roomId, mode]);

  const handleGuestJoin = (e: React.FormEvent) => {
    e.preventDefault();
    if (isJoining) return;
    setError(null);
    const finalName = name.trim() || 'Guest';
    grantTermsConsent();
    setIdentityName(finalName);
    trackEvent('room_joined', { roomId, mode: 'singer', provider: 'guest' });
    onJoin({ name: finalName, password: password || undefined });
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    if (isJoining) return;
    setError(null);
    try {
      const payload = JSON.parse(atob(credentialResponse.credential.split('.')[1]));
      const picture = payload.picture || '';
      const email = payload.email || '';
      const rawName = payload.name || payload.given_name || '';
      const googleName = rawName || (email ? email.split('@')[0] : 'Guest');

      const authResult = await exchangeGoogleToken(credentialResponse.credential);
      const user = authResult?.user;
      const userId = user?.id || `user-${Math.random().toString(36).substring(2, 10)}`;

      setIdentity({
        id: userId,
        name: googleName,
        avatarUrl: user?.avatar_url || picture,
        email: user?.email || email || undefined,
      });
      identifyUser(userId, { name: googleName, email: user?.email || email });
      grantTermsConsent();
      trackEvent('room_joined', { roomId, mode, provider: 'google' });

      onJoin({ name: googleName, password: password || undefined, user: user || undefined });
    } catch (err) {
      setError(t('room.error.googleLoginTryAgain'));
    }
  };

  const displayError = externalError || error;

  const mutedStyle: React.CSSProperties = { color: 'var(--text-muted)' };
  const errorStyle: React.CSSProperties = { color: 'var(--danger)' };

  if (loading) {
    return (
      <div className="landing-page">
        <div className="landing-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div className="card" style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
            <p style={mutedStyle}>{t('console.joinRoom.checkingRoom')}</p>
          </div>
        </div>
      </div>
    );
  }

  const isHostMode = mode === 'host';
  const needsPassword = room?.isProtected ?? false;
  const roomDoesNotExist = room && !room.exists;

  return (
    <div className="landing-page">
      <div className="room-hero-glow" aria-hidden="true" />
      <div className="landing-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', maxWidth: '90%', margin: '0 auto' }}>
        <div className="card">
          {isJoining && (
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                borderRadius: 'inherit',
                backgroundColor: 'rgba(10, 10, 18, 0.55)',
                backdropFilter: 'blur(2px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
              }}
            >
              <span className="spinner" style={{ width: 32, height: 32 }} />
              <span style={{ ...mutedStyle, fontSize: '0.875rem' }}>{t('console.joinRoom.joiningRoom')}</span>
            </div>
          )}
          <KaraokrLogo height={48} style={{ marginBottom: 24 }} />

          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: 8 }}>
            {isHostMode ? t('console.joinRoom.hostAccessTitle') : t('console.joinRoom.joinStageTitle')}
          </h1>
          <p style={{ ...mutedStyle, marginBottom: 20 }}>
            {t('console.joinRoom.roomLabel', { roomId })}
          </p>

          {displayError && <p style={{ ...errorStyle, marginBottom: 16 }}>{displayError}</p>}

          {isJoining && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 16 }}>
              <span className="spinner" />
              <span style={mutedStyle}>{t('console.joinRoom.joiningRoom')}</span>
            </div>
          )}

          {isHostMode && roomDoesNotExist && (
            <>
              <p style={{ ...mutedStyle, marginBottom: 24 }}>{t('console.joinRoom.roomNotFoundCreate')}</p>
              <div style={{ margin: '0 auto', display: 'flex', justifyContent: 'center', marginBottom: 16, opacity: isJoining ? 0.5 : 1, pointerEvents: isJoining ? 'none' : 'auto' }}>
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError(t('room.error.googleLoginFailed'))}
                  width='96%'
                />
              </div>
            </>
          )}

          {isHostMode && room?.exists && (
            <>
              <p style={{ ...mutedStyle, marginBottom: 24 }}>{t('console.joinRoom.signInToManage')}</p>
              {needsPassword && (
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('common.roomPassword')}
                  disabled={isJoining}
                  style={{ width: '100%' }}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'center', opacity: isJoining ? 0.5 : 1, pointerEvents: isJoining ? 'none' : 'auto' }}>
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError(t('room.error.googleLoginFailed'))}
                />
              </div>
              {onGoToSinger && (
                <button className="btn-secondary" style={{ width: '100%', marginTop: 16 }} onClick={onGoToSinger}>
                  {t('console.joinRoom.notHostButton')}
                </button>
              )}
            </>
          )}

          {!isHostMode && (
            <>
              <form onSubmit={handleGuestJoin} style={{ textAlign: 'left' }}>
                <label style={{ ...mutedStyle, fontSize: '0.875rem' }}>{t('common.yourName')}</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('common.displayNamePlaceholder')}
                  maxLength={60}
                  required
                  disabled={isJoining}
                  style={{ width: '100%', marginBottom: 12 }}
                  autoFocus
                />

                {needsPassword && (
                  <>
                    <label style={{ ...mutedStyle, fontSize: '0.875rem' }}>{t('common.roomPassword')}</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('console.joinRoom.passwordRequiredPlaceholder')}
                      required
                      disabled={isJoining}
                      style={{ width: '100%', marginBottom: 12 }}
                    />
                  </>
                )}

                <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={isJoining}>
                  {isJoining ? t('console.joinRoom.joiningRoom') : t('console.joinRoom.joinAsSingerButton')}
                </button>
              </form>

              <div className="join-room-divider" style={{ margin: '16px 0' }}>
                <span>{t('common.or')}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'center', opacity: isJoining ? 0.5 : 1, pointerEvents: isJoining ? 'none' : 'auto' }}>
                <GoogleLoginButton
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError(t('room.error.googleLoginFailed'))}
                />
              </div>

              <p style={{ ...mutedStyle, fontSize: '0.75rem', marginTop: 16 }}>
                {t('console.joinRoom.googleOptionalHint')}
              </p>
            </>
          )}

          <p style={{ ...mutedStyle, fontSize: '0.75rem', marginTop: 24, textAlign: 'center' }}>
            {t('consent.implicitNoticeLead')}{' '}
            <Link to="/terms" target="_blank" rel="noopener noreferrer" className="consent-link">
              {t('consent.termsLink')}
            </Link>{' '}
            {t('consent.and')}{' '}
            <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="consent-link">
              {t('consent.privacyLink')}
            </Link>
          </p>
        </div>
      </div>

    </div>
  );
}
